'use client';

import { useAtomSet, useAtomValue } from '@effect/atom-react';
import {
  Pagination,
  PaginationEllipsis,
  PaginationLoadMore,
  PaginationLoadMoreButton,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
} from '@vs-shop/commerce-ui/pagination';
import { generatePaginationRange } from '@vs-shop/domain/pagination';
import type { ListingScopeKey } from '@vs-shop/engine/capability/productListing/listingScope';
import { applyChange } from '@vs-shop/engine/capability/productListing/listingSelection';
import { encodeListingUrl } from '@vs-shop/engine/listing/codec';
import { href } from '@vs-shop/engine/listing/url/listingUrl';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import { Option } from 'effect';
import { useEffect, useRef, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { paginationSmallScreenPagesCount } from '../../../config/pagination';
import { RCPROFI_LISTING_SYNTAX } from '../../../effect/listing/listingConfig';
import { rcprofiListingJourney } from '../../../effect/listing/listingJourney';

type Props = { readonly scopeKey: ListingScopeKey };

/** Keeps addressable page links and append paging on the shared listing state. */
export const ListingNavigation = ({ scopeKey }: Props) => {
  const intl = useIntl();
  const binding = rcprofiListingJourney.bind(scopeKey);
  const current = useAtomValue(binding.current);
  const pendingPage = useRef<number | null>(null);
  const base = useAtomValue(binding.base);
  const vocabulary = useAtomValue(binding.vocabulary);
  const change = useAtomSet(binding.change, { mode: 'value' });
  const loadNextPage = binding.loadNextPage;
  if (loadNextPage === undefined) throw new Error('RCProfi append paging missing');
  const append = useAtomSet(loadNextPage, { mode: 'value' });

  useEffect(() => {
    AsyncResult.match(current, {
      onInitial: () => undefined,
      onFailure: () => undefined,
      onSuccess: ({ value: { state } }) => {
        if (pendingPage.current === null || state.page !== pendingPage.current) return;
        pendingPage.current = null;
        document.getElementById('listing-products-grid')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'center',
        });
      },
    });
  }, [current]);

  return Option.match(AsyncResult.value(current), {
    onNone: () => null,
    onSome: ({ state, products, failedPage }) => {
      const lastPage = products.lastPage;
      if (lastPage === undefined || lastPage <= 1) return null;
      const page = state.page;
      const range = generatePaginationRange(page, lastPage);
      const hrefFor = (target: number) =>
        href(
          encodeListingUrl(
            base,
            applyChange(state, { _tag: 'SetPage', page: target }),
            RCPROFI_LISTING_SYNTAX,
            vocabulary
          )
        );
      const onPage = (target: number) => (event: MouseEvent<HTMLAnchorElement>) => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        pendingPage.current = target;
        change({ _tag: 'SetPage', page: target });
      };

      return (
        <>
          <div data-slot="listing-navigation" className="flex w-full justify-center py-3">
            <Pagination
              aria-label={intl.formatMessage({
                id: 'container.pagination.pagination',
                defaultMessage: 'Paginace',
              })}
            >
              {page > 1 ? (
                <PaginationPrevious
                  href={hrefFor(page - 1)}
                  onClick={onPage(page - 1)}
                  aria-label={intl.formatMessage({
                    id: 'container.pagination.previousPage',
                    defaultMessage: 'Předchozí stránka',
                  })}
                />
              ) : null}
              {range.map((item, index) => {
                if (item.type === 'ellipsis') {
                  return (
                    <PaginationEllipsis key={`ellipsis-${String(index)}`}>...</PaginationEllipsis>
                  );
                }
                if (item.page === undefined) return null;
                return (
                  <PaginationPage
                    key={item.page}
                    page={item.page}
                    lastPage={lastPage}
                    smallScreenPagesCount={paginationSmallScreenPagesCount}
                    href={hrefFor(item.page)}
                    onClick={onPage(item.page)}
                    aria-current={item.page === page ? 'page' : undefined}
                    aria-label={intl.formatMessage(
                      {
                        id: 'container.pagination.pageNumber',
                        defaultMessage: 'Stránka {pageNumber}',
                      },
                      { pageNumber: item.page }
                    )}
                  >
                    {item.page}
                  </PaginationPage>
                );
              })}
              {page < lastPage ? (
                <PaginationNext
                  href={hrefFor(page + 1)}
                  onClick={onPage(page + 1)}
                  aria-label={intl.formatMessage({
                    id: 'container.pagination.nextPage',
                    defaultMessage: 'Následující stránka',
                  })}
                />
              ) : null}
            </Pagination>
          </div>
          {page < lastPage ? (
            <PaginationLoadMore className="flex-col items-center gap-2">
              {Option.match(failedPage, {
                onNone: () => null,
                onSome: () => (
                  <p data-slot="listing-load-more-error" role="alert">
                    {intl.formatMessage({
                      id: 'container.pagination.loadMorePagination.error',
                      defaultMessage: 'Další produkty se nepodařilo načíst. Zkuste to znovu.',
                    })}
                  </p>
                ),
              })}
              <PaginationLoadMoreButton
                onClick={() => append(undefined)}
                loading={current.waiting}
                disabled={current.waiting}
              >
                {Option.match(failedPage, {
                  onNone: () =>
                    intl.formatMessage({
                      id: 'container.pagination.loadMorePagination.showMore',
                      defaultMessage: 'ZOBRAZIT DALŠÍ',
                    }),
                  onSome: () =>
                    intl.formatMessage({
                      id: 'component.client.error.globalErrorContent.button.retry',
                      defaultMessage: 'Zkusit znovu',
                    }),
                })}
              </PaginationLoadMoreButton>
            </PaginationLoadMore>
          ) : null}
        </>
      );
    },
  });
};
